import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { DeliveryService, type SmsPreview, type TestSmsResult } from "@services/delivery/deliveryService";

/*
    Sending one real text to a number you type in (MPN-1717).

    The counter under the box is the reason this card is not just two fields and a button. SMS
    is billed per segment, and the segment count is not the character count: one curly quote,
    en dash or emoji forces the whole message from GSM-7 to UCS-2 and drops the per-segment
    budget from 160 characters to 70. A 150-character reminder that looks fine on screen bills
    as three segments because somebody pasted from Word. Nothing about the text shows that, so
    the count is read back from the server rather than guessed at here.

    No account picker, unlike the email card. Until A2P 10DLC registration completes per
    client there is no Twilio account row to choose between: sending goes through the
    platform's credentials, and requiring an account would block testing on the very setup
    step this card exists to test.
*/

// Twilio's own ceiling for a sane test. Past this somebody is checking something they will
// never send.
const MAX_LENGTH = 1200;
const SAMPLE = "Hi {{first_name}}, registration closes on the 14th. Reply STOP to opt out.";

// E.164 as Twilio wants it. Checked here only to keep an obviously wrong number from costing
// a round trip; the server refuses it again and Twilio is the real authority.
const looksLikeANumber = (value: string) => /^\+\d{8,15}$/.test(value.trim());

// What people actually type. Twilio takes none of these.
const tidy = (value: string) => {
  const digits = value.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return digits;
};

export const SmsTestCard = ({ organizationId }: { organizationId?: number | null }) => {
  const [to, setTo] = useState("");
  const [body, setBody] = useState(SAMPLE);
  const [preview, setPreview] = useState<SmsPreview | null>(null);
  const [result, setResult] = useState<TestSmsResult | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = new DeliveryService();

  useEffect(() => {
    if (!body.trim()) {
      setPreview(null);
      return;
    }
    // Debounced. The answer only changes when the text does, and a request per keystroke
    // would be a request per keystroke.
    let cancelled = false;
    const timer = setTimeout(() => {
      service
        .smsPreview(body)
        .then((p) => !cancelled && setPreview(p))
        .catch(() => !cancelled && setPreview(null));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [body]);

  const send = async () => {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      setResult(await service.testSms({ to: tidy(to), body, organizationId }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "The test text failed.");
    } finally {
      setSending(false);
    }
  };

  const ready = looksLikeANumber(tidy(to)) && body.trim().length > 0 && body.length <= MAX_LENGTH;

  return (
    <Card className="mb-4">
      <Card.Header>Send a test text</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          Sends one real text through Twilio to any mobile number. It arrives marked
          <code className="mx-1">[Test]</code>
          so whoever gets it knows what it is.
        </p>

        <div className="d-flex gap-3 align-items-start flex-wrap mb-3">
          <Form.Group controlId="sms-test-to" style={{ minWidth: 240 }}>
            <Form.Label>Send to</Form.Label>
            <Form.Control
              type="tel"
              value={to}
              placeholder="+1 202 555 0123"
              onChange={(e) => setTo(e.target.value)}
              onBlur={() => setTo(tidy(to))}
            />
            <Form.Text className="text-muted">
              {to && !looksLikeANumber(tidy(to))
                ? "Needs a country code, for example +12025550123."
                : "Mobile numbers only. A landline is charged for and never arrives."}
            </Form.Text>
          </Form.Group>
        </div>

        <Form.Group controlId="sms-test-body" className="mb-2">
          <Form.Label>Message</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={SAMPLE}
          />
        </Form.Group>

        <div className="d-flex align-items-center gap-3 flex-wrap mb-3">
          <span className="text-muted small">
            {preview ? (
              <>
                {preview.characters} characters,{" "}
                <strong>
                  {preview.segments} segment{preview.segments === 1 ? "" : "s"}
                </strong>
                {preview.remaining > 0 && ` (${preview.remaining} left in this one)`}
              </>
            ) : (
              `${body.length} characters`
            )}
          </span>
          {preview?.unicode && (
            <Badge bg="warning" text="dark">
              Unicode: 70 characters per segment, not 160
            </Badge>
          )}
        </div>

        {preview?.unicode && (
          <Alert variant="warning" className="py-2 small">
            A character outside the basic GSM set, usually a curly quote, en dash or emoji
            pasted from a word processor, has pushed this message to Unicode. Every segment now
            holds 70 characters instead of 160, so it costs more to send. Retyping the
            punctuation usually fixes it.
          </Alert>
        )}

        <div className="mb-3">
          <Button onClick={send} disabled={sending || !ready}>
            {sending ? <Spinner size="sm" animation="border" /> : "Send test text"}
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {result && (
          <div className="mt-2">
            <Badge bg="success">Sent</Badge>{" "}
            <span className="text-muted small">
              to {result.to} from {result.from_number}, billed as {result.segments} segment
              {result.segments === 1 ? "" : "s"}. Twilio reports{" "}
              <strong>{result.status}</strong>, which means it was accepted rather than that a
              handset has shown it.
            </span>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
              Message {result.sid}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
